import { POLLY_LANGUAGE_OPTIONS } from '../../../data/pollyVoices';
import { HintTooltip } from '../../HintTooltip';
import { localizedTextFieldTestId, type LocalizedText } from '../../../utils/localizedSsml';

/**
 * 線区愛称の言語別入力欄 — Issue #579 (SBI-09)。
 *
 * 受け入れ条件「路線名の任意設定から各言語での名称を案内できること」に対応する UI。
 * 日本語の愛称欄 (`route_nickname`) の直下に置き、放送言語として選択されている
 * 言語 (`ja` 以外) の名称を入力させる。
 *
 * ## なぜ {@link LocalizedTextFields} を再利用しないのか
 *
 * - 線区名は **1 行の固有名詞** なので `<textarea rows={2}>` ではなく `<input>` が適切。
 * - 「日本語から翻訳」ボタン (機械翻訳) は路線名に対して有害。「湘南新宿ライン」を
 *   機械翻訳すると意味訳になり、実際の英語表記 (`Shonan-Shinjuku Line`) と
 *   一致しない。正式な英字表記は事業者が定めるものなので **人が入力する**。
 * - 未入力時の挙動 (placeholder) の意味も違う。文面は「定型文マスタへ委譲」だが、
 *   愛称は「日本語の愛称でそのまま案内される」ため、日本語の値を薄字で示す。
 *
 * ## 空欄の扱い
 *
 * 空欄は「未設定」で保存されず、放送側は `ja` → `route_nickname` の順に
 * フォールバックする (放送を無音にしない / 後方互換)。
 */
export function LocalizedRouteNicknameFields({
  label,
  languages,
  value,
  japaneseNickname,
  testIdPrefix,
  onChange,
  summary = '各言語の路線名',
  hint,
  required = false,
}: {
  /**
   * 折りたたみの見出し。既定は「各言語の路線名」。
   * 方面名 (モンキーテスト B-1) など、固有名詞の言語別入力欄を同じ部品で出すときに差し替える。
   */
  summary?: string;
  /** 見出し下の説明文。未指定なら線区愛称向けの既定の説明を出す。 */
  hint?: React.ReactNode;
  /**
   * 各言語の名称を必須として表示するか (見出しに赤い * を付ける)。
   * 接続線の線区愛称は必須なので `true` を渡す。
   * 実際の入力チェックは `collectRequiredRouteNicknameIssues` (保存時) が担う。
   */
  required?: boolean;
  /** 入力欄の見出し (例: 「接続線 線区愛称」)。`aria-label` にも使う。 */
  label: string;
  /** 放送地点で選択されている放送言語 (`BroadcastPoint.languages`)。 */
  languages: readonly string[];
  /** 現在の `route_nickname_i18n`。 */
  value?: LocalizedText;
  /**
   * 日本語の愛称 (`route_nickname` の値)。
   *
   * 未入力の言語欄の placeholder として薄字表示し、「空欄のままだとこの名称で
   * 案内される」ことが分かるようにする (フォールバック規約の可視化)。
   */
  japaneseNickname?: string;
  /** `data-testid` を一意にするための接頭辞 (1 画面に複数欄が並ぶため)。 */
  testIdPrefix: string;
  onChange: (value: LocalizedText) => void;
}): React.JSX.Element | null {
  // 日本語は `route_nickname` (既存フィールド) が担うので、ここでは扱わない。
  const selectedLanguages = POLLY_LANGUAGE_OPTIONS.filter(
    (language) => language.key !== 'ja' && languages.includes(language.key),
  );
  // 放送言語が日本語のみなら入力欄を出さない (不要な欄で画面を埋めない)。
  if (selectedLanguages.length === 0) return null;

  const localized: LocalizedText = { ...value };
  return (
    <details open style={{ margin: '8px 0', minWidth: 0 }}>
      <summary>
        {summary}
        <HintTooltip label={`${label}の説明`} testId={`${testIdPrefix}-hint`}>
          {hint ?? (
            <>
              放送言語ごとに <code>{'{route_name}'}</code> / <code>{'{route}'}</code>{' '}
              {required
                ? 'へ展開する名称です。線区を選ぶと自動で入力されます。空欄のままだと日本語が放送されるため、必ず入力してください。'
                : 'へ展開する名称を設定できます。 未入力の言語は日本語の愛称でそのまま案内されます。'}
            </>
          )}
        </HintTooltip>
      </summary>
      {selectedLanguages.map((language) => {
        const testId = localizedTextFieldTestId(testIdPrefix, language.key);
        return (
          <label key={language.key} style={{ display: 'block', marginTop: 8 }}>
            <span className={required ? 'required' : undefined}>{language.label}</span>
            <input
              type="text"
              aria-label={`${label} ${language.label}`}
              data-testid={testId}
              style={{ width: '100%', boxSizing: 'border-box' }}
              value={localized[language.key] ?? ''}
              // 未入力時に実際に案内される名称 (= 日本語の愛称) を薄字で提示する。
              placeholder={japaneseNickname || undefined}
              onChange={(event) => onChange({ ...localized, [language.key]: event.target.value })}
            />
          </label>
        );
      })}
    </details>
  );
}
